import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type FeatureKey =
  | "analyze_chart"
  | "analyze_index"
  | "analyze_memecoin"
  | "rank_snipes"
  | "trade_coach"
  | "news_sentiment"
  | "cot_interpret"
  | "cot_backtest"
  | "memecoin_sentiment"
  | "top_news";

export interface CreditSummary {
  plan_id: string | null;
  sub_credits: number;
  sub_credits_granted: number;
  topup_credits: number;
  period_start: string | null;
  period_end: string | null;
  used_30d: number;
  per_feature_30d: Record<string, number>;
}

export interface CreditsState {
  loading: boolean;
  summary: CreditSummary | null;
  costs: Record<string, number>;
  total: number;
  refresh: () => Promise<void>;
  canAfford: (feature: FeatureKey) => boolean;
  costFor: (feature: FeatureKey) => number;
}

const CreditsContext = createContext<CreditsState | null>(null);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [costs, setCosts] = useState<Record<string, number>>({});

  const refresh = useCallback(async () => {
    if (!userId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [summaryRes, costsRes] = await Promise.all([
        supabase.rpc("get_credit_summary"),
        supabase.from("feature_costs").select("feature,credits"),
      ]);
      if (!summaryRes.error && summaryRes.data) {
        const d = summaryRes.data as any;
        if (d.ok) {
          setSummary({
            plan_id: d.plan_id ?? null,
            sub_credits: d.sub_credits ?? 0,
            sub_credits_granted: d.sub_credits_granted ?? 0,
            topup_credits: d.topup_credits ?? 0,
            period_start: d.period_start ?? null,
            period_end: d.period_end ?? null,
            used_30d: d.used_30d ?? 0,
            per_feature_30d: d.per_feature_30d ?? {},
          });
        }
      }
      if (!costsRes.error && costsRes.data) {
        const map: Record<string, number> = {};
        for (const c of costsRes.data as any[]) map[c.feature] = c.credits;
        setCosts(map);
      }
    } catch (e) {
      console.error("[useCredits] refresh failed", e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`credit_balances_${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "credit_balances", filter: `user_id=eq.${userId}` },
        () => {
          void refresh();
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const total = (summary?.sub_credits ?? 0) + (summary?.topup_credits ?? 0);

  const costFor = useCallback(
    (feature: FeatureKey) => costs[feature] ?? 0,
    [costs],
  );

  const canAfford = useCallback(
    (feature: FeatureKey) => {
      const c = costFor(feature);
      return total >= c;
    },
    [total, costFor],
  );

  return (
    <CreditsContext.Provider value={{ loading, summary, costs, total, refresh, canAfford, costFor }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits(): CreditsState {
  const ctx = useContext(CreditsContext);
  if (!ctx) {
    // Fallback for components rendered outside provider — return safe defaults
    return {
      loading: false,
      summary: null,
      costs: {},
      total: 0,
      refresh: async () => {},
      canAfford: () => false,
      costFor: () => 0,
    };
  }
  return ctx;
}
